import type { ScopeChipColor } from './ScopeCell';

export type ScopeSource = 'explicit' | 'inferred_all' | 'needs_review';

export interface RoomsScopeResult {
  matchedRooms: Array<string>;
  scope: 'all' | 'only' | 'except';
  source: ScopeSource;
}

export interface DaysScopeResult {
  matchedDays: Array<string>;
  source: ScopeSource;
}

const ALL_ROOMS_PATTERN = /\ball\s+room(?:s|\s+types?)?\b/i;
const EXCEPT_PATTERN = /\b(?:except|excluding)\b/i;
const ROOM_TERM_PATTERN = /room|bedroom|studio|\bbr\b|suite|villa|apartment/i;
const TRAILING_ORDINAL_PATTERN = /\s+\d+$/;
const LEADING_ORDINAL_PATTERN = /^\d+\s*-\s*/;
const NOISE_QUALIFIER_PATTERN = /\bonly\b|\(.*?\)/gi;
const TOKEN_PATTERN = /[a-z0-9]+/g;
// Words that appear in blackout names but carry no room-identifying signal on their own.
const GENERIC_WORDS = new Set([
  'room',
  'rooms',
  'type',
  'types',
  'only',
  'the',
  'a',
  'and',
  'all',
  'stop',
  'sell',
  'black',
  'out',
  'blackout',
  'archived',
]);

const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_PATTERN = new RegExp(`\\b(${DAY_NAMES.join('|')})s?\\b`, 'gi');
// "Fridayz", "Good Friday", "AFL Weekend" name an event/holiday, not a day-of-week rule.
const DAY_FALSE_POSITIVE_PATTERN = /\bgood\s+friday\b|fridayz/i;

function stripRoomNameNoise(text: string): string {
  return text
    .replace(TRAILING_ORDINAL_PATTERN, '')
    .replace(LEADING_ORDINAL_PATTERN, '')
    .replace(NOISE_QUALIFIER_PATTERN, '')
    .trim();
}

const NUMBER_WORD_TO_DIGIT: Record<string, string> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
};

function tokenize(text: string): Array<string> {
  const rawTokens = text.toLowerCase().match(TOKEN_PATTERN) ?? [];
  return rawTokens.map((token) => NUMBER_WORD_TO_DIGIT[token] ?? token);
}

function distinctiveTokens(text: string): Array<string> {
  return tokenize(text).filter((token) => !GENERIC_WORDS.has(token));
}

function findMatchingRoomNames(candidateText: string, roomTypeNames: Array<string>): Array<string> {
  const candidateTokens = distinctiveTokens(candidateText);

  if (!candidateTokens.length) {
    return [];
  }

  const candidateTokenSet = new Set(candidateTokens);
  const exactMatches = roomTypeNames.filter((roomTypeName) => {
    const roomTypeTokenSet = new Set(distinctiveTokens(roomTypeName));
    return (
      roomTypeTokenSet.size === candidateTokenSet.size && candidateTokens.every((token) => roomTypeTokenSet.has(token))
    );
  });

  if (exactMatches.length) {
    return exactMatches;
  }

  // No room name matches the candidate's words exactly — fall back to the room
  // names whose words are a superset (e.g. "2 Bedroom" candidate vs "2 Bedroom Apartment" room).
  return roomTypeNames.filter((roomTypeName) => {
    const roomTypeTokens = tokenize(roomTypeName);
    return candidateTokens.every((token) => roomTypeTokens.includes(token));
  });
}

export function classifyRoomsScope(name: string, roomTypeNames: Array<string>): RoomsScopeResult {
  if (ALL_ROOMS_PATTERN.test(name) && !EXCEPT_PATTERN.test(name)) {
    return { matchedRooms: [], scope: 'all', source: 'explicit' };
  }

  const scope = EXCEPT_PATTERN.test(name) ? 'except' : 'only';
  const [, textAfterKeyword] = EXCEPT_PATTERN.test(name) ? name.split(EXCEPT_PATTERN) : [null, name];
  const candidateText = stripRoomNameNoise(textAfterKeyword ?? name);
  const matchedRooms = findMatchingRoomNames(candidateText, roomTypeNames);

  if (matchedRooms.length) {
    return { matchedRooms, scope, source: 'explicit' };
  }

  if (ROOM_TERM_PATTERN.test(name)) {
    return { matchedRooms: [], scope, source: 'needs_review' };
  }

  return { matchedRooms: [], scope: 'all', source: 'inferred_all' };
}

export function classifyDaysScope(name: string): DaysScopeResult {
  if (DAY_FALSE_POSITIVE_PATTERN.test(name)) {
    return { matchedDays: [], source: 'inferred_all' };
  }

  const matches = Array.from(name.matchAll(DAY_PATTERN)).map((match) => match[1].toLowerCase());

  if (!matches.length) {
    return { matchedDays: [], source: 'inferred_all' };
  }

  const matchedDays = Array.from(new Set(matches)).map((day) => day.charAt(0).toUpperCase() + day.slice(1));

  return { matchedDays, source: 'explicit' };
}

export const SCOPE_SOURCE_LABEL: Record<ScopeSource, string> = {
  explicit: 'From name',
  inferred_all: 'Assumed (no restriction stated)',
  needs_review: 'Needs review',
};

export const ROOM_SCOPE_SOURCE_TOOLTIP: Record<ScopeSource, string> = {
  explicit: 'Explicit room name in SF blackout name',
  inferred_all: 'No explicit room name in SF blackout name, assumed to apply to all',
  needs_review: 'Ambiguous room name in SF blackout name',
};

export function getRoomsScopeChipColor(result: RoomsScopeResult): ScopeChipColor {
  if (result.source === 'needs_review') {
    return 'warning';
  }

  if (result.scope === 'except') {
    return 'secondary';
  }

  return result.scope === 'only' ? 'primary' : 'default';
}

export function getDaysScopeChipColor(result: DaysScopeResult): ScopeChipColor {
  return result.source === 'explicit' ? 'info' : 'default';
}

export function formatRoomsScope(result: RoomsScopeResult): string {
  if (result.scope === 'all') {
    return 'All room types';
  }

  if (!result.matchedRooms.length) {
    return 'Unable to determine';
  }

  const roomList = result.matchedRooms.join(', ');
  return result.scope === 'except' ? `All room types except ${roomList}` : roomList;
}

export function formatDaysScope(result: DaysScopeResult): string {
  return result.matchedDays.length ? result.matchedDays.join(', ') : 'All days in range';
}

// Shared by the read-only tab and the apply-plan builder so scope logic stays consistent.
export function blackoutAppliesToRoomType(
  blackoutName: string,
  roomTypeName: string,
  roomTypeNames: Array<string>,
): boolean {
  const rooms = classifyRoomsScope(blackoutName, roomTypeNames);

  if (rooms.source === 'needs_review' || rooms.scope === 'all') {
    return true;
  }

  const isMatchedRoom = rooms.matchedRooms.includes(roomTypeName);
  return rooms.scope === 'except' ? !isMatchedRoom : isMatchedRoom;
}
